/**
 * QuickBill Printer Utility
 * Comprehensive Bluetooth printer management and ESC/POS command generation
 * 
 * @author QuickBill Team
 * @version 2.0.0
 * 
 * Features:
 * - Bluetooth printer discovery and connection management
 * - ESC/POS command generation for thermal printers
 * - Receipt formatting with GST compliance
 * - Auto-reconnection and error recovery
 * - Support for 58mm and 80mm paper widths
 * - Event-driven architecture for UI integration
 */

// ESC/POS Command Constants
const ESC = '\x1B'
const GS = '\x1D'

// Template Engine Import (loaded dynamically to avoid circular dependencies)
let templateEngine = null
async function getTemplateEngine() {
  if (!templateEngine) {
    const module = await import('./receiptTemplates.js')
    templateEngine = module.templateEngine
  }
  return templateEngine
}

const ESCPOS_COMMANDS = {
  // Text formatting
  RESET: ESC + '@',
  BOLD_ON: ESC + 'E' + '\x01',
  BOLD_OFF: ESC + 'E' + '\x00',
  UNDERLINE_ON: ESC + '-' + '\x01',
  UNDERLINE_OFF: ESC + '-' + '\x00',
  
  // Text alignment
  ALIGN_LEFT: ESC + 'a' + '\x00',
  ALIGN_CENTER: ESC + 'a' + '\x01',
  ALIGN_RIGHT: ESC + 'a' + '\x02',
  
  // Text size
  SIZE_NORMAL: GS + '!' + '\x00',
  SIZE_DOUBLE_HEIGHT: GS + '!' + '\x01',
  SIZE_DOUBLE_WIDTH: GS + '!' + '\x10',
  SIZE_DOUBLE_BOTH: GS + '!' + '\x11',
  
  // Line spacing and feeds
  LINE_FEED: '\n',
  FORM_FEED: '\f',
  CARRIAGE_RETURN: '\r',
  
  // Paper handling
  CUT_PAPER: GS + 'V' + '\x00',
  CUT_PAPER_PARTIAL: GS + 'V' + '\x01',
  
  // Printer control
  BEEP: ESC + 'B' + '\x05' + '\x05',
  
  // Character sets (to prevent Chinese character display)
  CHARSET_USA: ESC + 'R' + '\x00',        // USA character set
  CHARSET_FRANCE: ESC + 'R' + '\x01',     // France character set
  CHARSET_GERMANY: ESC + 'R' + '\x02',    // Germany character set
  CHARSET_UK: ESC + 'R' + '\x03',         // UK character set
  CHARSET_DENMARK: ESC + 'R' + '\x04',    // Denmark character set
  CHARSET_SWEDEN: ESC + 'R' + '\x05',     // Sweden character set
  CHARSET_ITALY: ESC + 'R' + '\x06',      // Italy character set
  CHARSET_SPAIN: ESC + 'R' + '\x07',      // Spain character set
  
  // Code pages (for better international support)
  CODEPAGE_CP437: ESC + 't' + '\x00',     // USA, Standard Europe
  CODEPAGE_CP850: ESC + 't' + '\x02',     // Multilingual
  CODEPAGE_CP858: ESC + 't' + '\x13',     // Multilingual with Euro
  CODEPAGE_CP1252: ESC + 't' + '\x10',    // Windows Latin-1
  OPEN_DRAWER: ESC + 'p' + '\x00' + '\x19' + '\xFA'
}

/**
 * Custom error classes for better error handling
 */
class PrinterError extends Error {
  constructor(message, code = 'PRINTER_ERROR') {
    super(message)
    this.name = 'PrinterError'
    this.code = code
  }
}

class BluetoothError extends PrinterError {
  constructor(message, code = 'BLUETOOTH_ERROR') {
    super(message, code)
    this.name = 'BluetoothError'
  }
}

class ConnectionError extends PrinterError {
  constructor(message, code = 'CONNECTION_ERROR') {
    super(message, code)
    this.name = 'ConnectionError'
  }
}

/**
 * Bluetooth Printer Manager
 * Handles all Bluetooth operations including discovery, connection, and communication
 */
class BluetoothPrinterManager extends EventTarget {
  constructor(options = {}) {
    super()
    this.device = null
    this.characteristic = null
    this.isConnected = false
    this.isConnecting = false
    this.autoReconnect = options.autoReconnect ?? true
    this.connectionTimeout = options.connectionTimeout ?? 10000
    this.retryAttempts = options.retryAttempts ?? 3
    this.debug = options.debug ?? false
    
    // Bluetooth service UUIDs for thermal printers
    this.serviceUUID = '000018f0-0000-1000-8000-00805f9b34fb'
    this.characteristicUUID = '00002af1-0000-1000-8000-00805f9b34fb'
  }

  /**
   * Log debug information if debug mode is enabled
   */
  _log(message, data = null) {
    // Debug logging removed for production
  }

  /**
   * Check if Web Bluetooth is supported
   */
  isBluetoothSupported() {
    return 'bluetooth' in navigator && 'requestDevice' in navigator.bluetooth
  }

  /**
   * Discover available Bluetooth printers
   * @returns {Promise<BluetoothDevice[]>} Array of discovered devices
   */
  async discoverPrinters() {
    if (!this.isBluetoothSupported()) {
      throw new BluetoothError('Web Bluetooth is not supported in this browser')
    }

    this._log('Starting printer discovery...')
    this.dispatchEvent(new CustomEvent('scanning', { detail: true }))

    try {
      const devices = []
      
      // Enhanced device request to support SR20 and all printer types
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          this.serviceUUID, // Standard thermal printer service
          '0000fff1-0000-1000-8000-00805f9b34fb', // SR20 custom UUID
          '0000fff2-0000-1000-8000-00805f9b34fb', // SR20 characteristic UUID
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Epson printers
          '0000ae30-0000-1000-8000-00805f9b34fb', // Another common thermal
          '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART service
          '0000180f-0000-1000-8000-00805f9b34fb', // Battery service
          '0000180a-0000-1000-8000-00805f9b34fb'  // Device information service
        ]
      })

      if (device) {
        devices.push(device)
        
        // Enhanced logging for better device identification
        const isSR20 = device.name && device.name.toLowerCase().includes('sr20')
        this._log('Discovered printer:', {
          name: device.name || 'Unknown Device',
          id: device.id,
          isSR20: isSR20,
          uuids: device.uuids || 'Not available'
        })
        
        if (isSR20) {
          this._log('🎯 SR20 printer detected in discovery!')
        }
      }

      this.dispatchEvent(new CustomEvent('scanning', { detail: false }))
      this.dispatchEvent(new CustomEvent('devicesFound', { detail: devices }))

      return devices
    } catch (error) {
      this.dispatchEvent(new CustomEvent('scanning', { detail: false }))
      
      if (error.name === 'NotFoundError') {
        this._log('No printers found')
        return []
      }
      
      this._log('Discovery error:', error)
      throw new BluetoothError(`Failed to discover printers: ${error.message}`)
    }
  }

  /**
   * Connect to a specific Bluetooth device
   * @param {BluetoothDevice} device - The device to connect to
   * @returns {Promise<boolean>} Success status
   */
  async connectToDevice(device) {
    if (this.isConnecting) {
      throw new ConnectionError('Connection already in progress')
    }

    if (this.isConnected && this.device?.id === device.id) {
      this._log('Already connected to this device')
      return true
    }

    this.isConnecting = true
    this.dispatchEvent(new CustomEvent('connecting', { detail: device }))

    try {
      this._log(`Connecting to ${device.name}...`)

      // Disconnect from current device if connected
      if (this.isConnected) {
        await this.disconnect()
      }

      // Set connection timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new ConnectionError('Connection timeout')), this.connectionTimeout)
      })

      // Connect to GATT server
      const connectPromise = device.gatt.connect()
      const server = await Promise.race([connectPromise, timeoutPromise])

      this._log('GATT server connected, waiting before service discovery...')
      
      // Wait a moment for SR20 to stabilize (prevents "GATT Server is disconnected" error)
      const isSR20 = device.name && device.name.toLowerCase().includes('sr20')
      let service, characteristic
      
      if (isSR20) {
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Verify connection is still active before proceeding
        if (!server.connected) {
          this._log('⚠️ SR20 disconnected, attempting reconnect...')
          const retryServer = await device.gatt.connect()
          await new Promise(resolve => setTimeout(resolve, 300))
          
          if (!retryServer.connected) {
            throw new ConnectionError('SR20 failed to maintain connection')
          }
          
          // Use the reconnected server
          const result = await this.detectPrinterInterface(retryServer, device)
          service = result.service
          characteristic = result.characteristic
        } else {
          // Enhanced service and characteristic detection for SR20 and standard printers
          const result = await this.detectPrinterInterface(server, device)
          service = result.service
          characteristic = result.characteristic
        }
      } else {
        // For non-SR20 printers, proceed normally with a smaller delay
        await new Promise(resolve => setTimeout(resolve, 100))
        const result = await this.detectPrinterInterface(server, device)
        service = result.service
        characteristic = result.characteristic
      }
      
      this.characteristic = characteristic

      // Store device reference
      this.device = device
      this.isConnected = true
      this.isConnecting = false

      // Enhanced connection success logging
      const isCustomUUID = characteristic.service.uuid === '0000fff1-0000-1000-8000-00805f9b34fb'
      
      this._log(`✅ Successfully connected to ${device.name}`, {
        deviceType: isSR20 ? 'SR20 Printer' : 'Standard Printer',
        serviceUUID: characteristic.service.uuid,
        characteristicUUID: characteristic.uuid,
        isCustomUUID: isCustomUUID
      })

      // Listen for disconnection
      device.addEventListener('gattserverdisconnected', this._handleDisconnection.bind(this))

      // Store connection in localStorage for auto-reconnect
      if (this.autoReconnect) {
        localStorage.setItem('lastConnectedPrinter', JSON.stringify({
          id: device.id,
          name: device.name
        }))
      }

      this.dispatchEvent(new CustomEvent('connected', { detail: device }))
      return true

    } catch (error) {
      this.isConnecting = false
      this.isConnected = false
      this.device = null
      this.characteristic = null

      this._log('Connection failed:', error)
      this.dispatchEvent(new CustomEvent('connectionError', { detail: error }))

      throw new ConnectionError(`Failed to connect to ${device.name}: ${error.message}`)
    }
  }

  /**
   * Intelligently detect the correct printer service and characteristic for both standard and SR20 printers
   */
  async detectPrinterInterface(server, device) {
    this._log('🔍 Detecting printer interface for device:', device.name || 'Unknown')
    
    const services = await server.getPrimaryServices()
    this._log(`📋 Found ${services.length} services on device`)
    
    // Check if this is an SR20 printer
    const isSR20 = device.name && device.name.toLowerCase().includes('sr20')
    
    // Priority order: Try SR20 first if detected, then standard services
    const servicePriority = isSR20 ? [
      '0000fff1-0000-1000-8000-00805f9b34fb', // SR20 custom UUID (priority for SR20)
      this.serviceUUID, // Standard thermal printer
      '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Epson
      '0000ae30-0000-1000-8000-00805f9b34fb'  // Another common thermal
    ] : [
      this.serviceUUID, // Standard thermal printer (priority for others)
      '0000fff1-0000-1000-8000-00805f9b34fb', // SR20 custom UUID
      '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Epson
      '0000ae30-0000-1000-8000-00805f9b34fb'  // Another common thermal
    ]
    
    // Try priority services first
    for (const targetServiceUUID of servicePriority) {
      const service = services.find(s => s.uuid === targetServiceUUID)
      if (service) {
        this._log(`🎯 Found priority service: ${targetServiceUUID}`)
        
        try {
          const characteristics = await service.getCharacteristics()
          
          // For SR20, try specific characteristic first
          if (targetServiceUUID === '0000fff1-0000-1000-8000-00805f9b34fb') {
            const sr20Char = characteristics.find(c => c.uuid === '0000fff2-0000-1000-8000-00805f9b34fb')
            if (sr20Char && (sr20Char.properties.write || sr20Char.properties.writeWithoutResponse)) {
              this._log(`✅ Found SR20 characteristic: ${sr20Char.uuid}`)
              return { service, characteristic: sr20Char }
            }
          }
          
          // For standard services, try the default characteristic
          if (targetServiceUUID === this.serviceUUID) {
            const standardChar = characteristics.find(c => c.uuid === this.characteristicUUID)
            if (standardChar && (standardChar.properties.write || standardChar.properties.writeWithoutResponse)) {
              this._log(`✅ Found standard characteristic: ${standardChar.uuid}`)
              return { service, characteristic: standardChar }
            }
          }
          
          // Fallback: find any writable characteristic
          const writableChar = characteristics.find(c => 
            c.properties.write || c.properties.writeWithoutResponse
          )
          
          if (writableChar) {
            this._log(`✅ Found writable characteristic: ${writableChar.uuid}`)
            return { service, characteristic: writableChar }
          }
          
        } catch (err) {
          this._log(`⚠️ Could not access service ${targetServiceUUID}:`, err.message)
        }
      }
    }
    
    // If priority services not found, scan all services for writable characteristics
    this._log('🔍 Priority services not found, scanning all services...')
    
    for (const service of services) {
      try {
        const characteristics = await service.getCharacteristics()
        const writableChar = characteristics.find(c => 
          c.properties.write || c.properties.writeWithoutResponse
        )
        
        if (writableChar) {
          this._log(`✅ Found fallback service: ${service.uuid} with characteristic: ${writableChar.uuid}`)
          return { service, characteristic: writableChar }
        }
      } catch (err) {
        this._log(`⚠️ Could not scan service ${service.uuid}:`, err.message)
      }
    }
    
    throw new Error('No writable characteristics found on this device. It may not be a supported printer.')
  }

  /**
   * Handle disconnection events
   */
  _handleDisconnection(event) {
    this._log('Device disconnected:', event.target.name)
    
    this.isConnected = false
    this.characteristic = null
    
    this.dispatchEvent(new CustomEvent('disconnected', { detail: this.device }))

    // Attempt auto-reconnection if enabled
    if (this.autoReconnect && this.device) {
      this._log('Attempting auto-reconnection...')
      setTimeout(() => this._attemptReconnection(), 2000)
    }
  }

  /**
   * Attempt to reconnect to the last connected device
   */
  async _attemptReconnection() {
    if (!this.device || this.isConnecting) return

    try {
      await this.connectToDevice(this.device)
    } catch (error) {
      this._log('Auto-reconnection failed:', error)
      this.dispatchEvent(new CustomEvent('reconnectionFailed', { detail: error }))
    }
  }

  /**
   * Disconnect from the current device
   */
  async disconnect() {
    if (!this.isConnected || !this.device) {
      this._log('No device to disconnect from')
      return
    }

    try {
      this._log(`Disconnecting from ${this.device.name}...`)
      
      if (this.device.gatt.connected) {
        this.device.gatt.disconnect()
      }
      
      this.isConnected = false
      this.characteristic = null
      this.device = null

      // Clear stored connection
      localStorage.removeItem('lastConnectedPrinter')

      this._log('Disconnected successfully')
      this.dispatchEvent(new CustomEvent('disconnected', { detail: null }))

    } catch (error) {
      this._log('Disconnection error:', error)
      throw new ConnectionError(`Failed to disconnect: ${error.message}`)
    }
  }

  /**
   * Send data to the connected printer
   * @param {Uint8Array} data - The data to send
   * @returns {Promise<void>}
   */
  async sendData(data) {
    if (!this.isConnected || !this.characteristic) {
      throw new ConnectionError('No printer connected')
    }

    try {
      // Split data into chunks if necessary (some devices have MTU limits)
      const chunkSize = 20
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize)
        await this.characteristic.writeValueWithoutResponse(chunk)
        
        // Small delay between chunks to prevent buffer overflow
        if (i + chunkSize < data.length) {
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      this._log(`Sent ${data.length} bytes to printer`)
    } catch (error) {
      this._log('Send data error:', error)
      throw new ConnectionError(`Failed to send data to printer: ${error.message}`)
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      device: this.device ? {
        id: this.device.id,
        name: this.device.name
      } : null
    }
  }
}

/**
 * ESC/POS Command Builder
 * Generates ESC/POS commands for thermal printers
 */
class ESCPOSCommandBuilder {
  constructor(options = {}) {
    this.paperWidth = options.paperWidth || 58 // 58mm or 80mm
    this.charWidth = this.paperWidth === 58 ? 32 : 48
    this.encoding = options.encoding || 'utf-8'
    this.useCharsetInit = options.useCharsetInit ?? true
    this.commands = []
    
    // Initialize with proper character set to prevent Chinese character display
    if (this.useCharsetInit) {
      this.initializeCharacterSet()
    }
  }

  /**
   * Initialize printer with proper character set
   */
  initializeCharacterSet() {
    // Set to USA character set and CP850 code page for better compatibility
    this.raw(ESCPOS_COMMANDS.CHARSET_USA)
    this.raw(ESCPOS_COMMANDS.CODEPAGE_CP850)
    return this
  }

  /**
   * Add raw command to buffer
   */
  raw(command) {
    this.commands.push(command)
    return this
  }

  /**
   * Reset printer to default settings
   */
  reset() {
    this.raw(ESCPOS_COMMANDS.RESET)
    
    // Reinitialize character set after reset
    if (this.useCharsetInit) {
      this.initializeCharacterSet()
    }
    
    return this
  }

  /**
   * Set text alignment
   */
  align(alignment = 'left') {
    const alignMap = {
      left: ESCPOS_COMMANDS.ALIGN_LEFT,
      center: ESCPOS_COMMANDS.ALIGN_CENTER,
      right: ESCPOS_COMMANDS.ALIGN_RIGHT
    }
    return this.raw(alignMap[alignment] || ESCPOS_COMMANDS.ALIGN_LEFT)
  }

  /**
   * Set text formatting
   */
  bold(enabled = true) {
    return this.raw(enabled ? ESCPOS_COMMANDS.BOLD_ON : ESCPOS_COMMANDS.BOLD_OFF)
  }

  underline(enabled = true) {
    return this.raw(enabled ? ESCPOS_COMMANDS.UNDERLINE_ON : ESCPOS_COMMANDS.UNDERLINE_OFF)
  }

  /**
   * Set text size
   */
  size(width = 1, height = 1) {
    let command = ESCPOS_COMMANDS.SIZE_NORMAL
    
    if (width === 2 && height === 1) {
      command = ESCPOS_COMMANDS.SIZE_DOUBLE_WIDTH
    } else if (width === 1 && height === 2) {
      command = ESCPOS_COMMANDS.SIZE_DOUBLE_HEIGHT
    } else if (width === 2 && height === 2) {
      command = ESCPOS_COMMANDS.SIZE_DOUBLE_BOTH
    }
    
    return this.raw(command)
  }

  /**
   * Add text with automatic line wrapping
   */
  text(content, options = {}) {
    if (options.bold) this.bold(true)
    if (options.underline) this.underline(true)
    if (options.align) this.align(options.align)
    if (options.size) this.size(options.size.width, options.size.height)

    // Split long lines
    const lines = this._wrapText(content.toString(), this.charWidth)
    
    for (const line of lines) {
      this.raw(line + ESCPOS_COMMANDS.LINE_FEED)
    }

    // Reset formatting
    if (options.bold || options.underline || options.size) {
      this.reset()
    }

    return this
  }

  /**
   * Add a line of text that fills the width
   */
  textLine(left = '', right = '', fillChar = ' ') {
    const totalWidth = this.charWidth
    const leftPart = left.toString()
    const rightPart = right.toString()
    const fillWidth = totalWidth - leftPart.length - rightPart.length
    
    if (fillWidth > 0) {
      const line = leftPart + fillChar.repeat(fillWidth) + rightPart
      this.raw(line + ESCPOS_COMMANDS.LINE_FEED)
    } else {
      // If content is too long, put right part on next line
      this.raw(leftPart + ESCPOS_COMMANDS.LINE_FEED)
      this.raw(rightPart.padStart(totalWidth) + ESCPOS_COMMANDS.LINE_FEED)
    }

    return this
  }

  /**
   * Add separator line
   */
  separator(char = '-') {
    return this.raw(char.repeat(this.charWidth) + ESCPOS_COMMANDS.LINE_FEED)
  }

  /**
   * Add line feeds
   */
  feed(lines = 1) {
    return this.raw(ESCPOS_COMMANDS.LINE_FEED.repeat(lines))
  }

  /**
   * Cut paper
   */
  cut(partial = false) {
    return this.raw(partial ? ESCPOS_COMMANDS.CUT_PAPER_PARTIAL : ESCPOS_COMMANDS.CUT_PAPER)
  }

  /**
   * Make beep sound
   */
  beep() {
    return this.raw(ESCPOS_COMMANDS.BEEP)
  }

  /**
   * Open cash drawer
   */
  openDrawer() {
    return this.raw(ESCPOS_COMMANDS.OPEN_DRAWER)
  }

  /**
   * Wrap text to fit printer width
   */
  _wrapText(text, width) {
    if (text.length <= width) return [text]
    
    const words = text.split(' ')
    const lines = []
    let currentLine = ''

    for (const word of words) {
      if (currentLine.length + word.length + 1 <= width) {
        currentLine += (currentLine ? ' ' : '') + word
      } else {
        if (currentLine) lines.push(currentLine)
        currentLine = word
      }
    }

    if (currentLine) lines.push(currentLine)
    return lines
  }

  /**
   * Build final command buffer
   * Enhanced to handle both text and binary data properly
   */
  build() {
    // Calculate total length for proper binary handling
    let totalLength = 0
    const processedCommands = []
    
    for (const command of this.commands) {
      if (command instanceof Uint8Array) {
        // Binary data (like images) - keep as-is
        processedCommands.push(command)
        totalLength += command.length
      } else if (typeof command === 'string') {
        // Text commands - encode to bytes
        const encoded = new TextEncoder().encode(command)
        processedCommands.push(encoded)
        totalLength += encoded.length
      } else {
        // Convert other types to string first
        const encoded = new TextEncoder().encode(String(command))
        processedCommands.push(encoded)
        totalLength += encoded.length
      }
    }
    
    // Create final buffer and copy all data
    const finalBuffer = new Uint8Array(totalLength)
    let offset = 0
    
    for (const command of processedCommands) {
      finalBuffer.set(command, offset)
      offset += command.length
    }
    

    return finalBuffer
  }

  /**
   * Clear command buffer
   */
  clear() {
    this.commands = []
    return this
  }
}

/**
 * Receipt Formatter
 * Formats transaction data into a printable receipt
 */
class ReceiptFormatter {
  constructor(options = {}) {
    this.paperWidth = options.paperWidth || 58
    this.business = options.business || {}
    this.includeGST = options.includeGST ?? true
    this.includeBranding = options.includeBranding ?? true
  }

  /**
   * Format a complete receipt
   */
  formatReceipt(transaction, options = {}) {
    const builder = new ESCPOSCommandBuilder({
      paperWidth: this.paperWidth
    })

    builder.reset()

    // Header
    this._addHeader(builder, transaction)
    
    // Customer info
    this._addCustomerInfo(builder, transaction)
    
    // Items
    this._addItems(builder, transaction)
    
    // Totals
    this._addTotals(builder, transaction)
    
    // Footer
    this._addFooter(builder, transaction)

    // Cut paper
    if (options.cut !== false) {
      builder.feed(3).cut()
    }

    return builder.build()
  }

  /**
   * Add receipt header
   */
  _addHeader(builder, transaction) {
    // Business name
    if (this.business.name) {
      builder.align('center')
        .bold(true)
        .size(2, 1)
        .text(this.business.name)
        .reset()
    }

    // Business address
    if (this.business.address) {
      builder.align('center')
        .text(this.business.address)
    }

    // Contact info
    if (this.business.phone || this.business.email) {
      const contact = [this.business.phone, this.business.email]
        .filter(Boolean)
        .join(' | ')
      builder.align('center').text(contact)
    }

    // GSTIN
    if (this.business.gstin && this.includeGST) {
      builder.align('center')
        .text(`GSTIN: ${this.business.gstin}`)
    }

    builder.separator('=')
  }

  /**
   * Add customer information
   */
  _addCustomerInfo(builder, transaction) {
    // Invoice number and date
    builder.textLine(
      `Bill: ${transaction.invoice_number || transaction._id?.slice(-6) || 'N/A'}`,
      new Date(transaction.date || transaction.created_at).toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    )

    // Customer info
    if (transaction.customer_name && transaction.customer_name !== 'Walk-in Customer') {
      builder.text(`Customer: ${transaction.customer_name}`)
      
      if (transaction.customer_mobile) {
        builder.text(`Mobile: ${transaction.customer_mobile}`)
      }
    }

    builder.separator()
  }

  /**
   * Add items section
   */
  _addItems(builder, transaction) {
    // Items header
    builder.bold(true)
      .textLine('Item', 'Amount')
      .reset()
      .separator()

    // Items
    for (const item of transaction.items || []) {
      const name = item.product_name || item.name || 'Item'
      const qty = item.quantity || 1
      const price = item.unit_price || item.price || 0
      const total = item.total_price || (qty * price)

      // Item name
      builder.text(name)
      
      // Quantity and price
      builder.textLine(
        `${qty} x ₹${price.toFixed(2)}`,
        `₹${total.toFixed(2)}`
      )

      // GST details if applicable
      if (this.includeGST && item.gst_rate?.total > 0) {
        const gstAmount = (total * item.gst_rate.total) / 100
        builder.align('right')
          .text(`(GST ${item.gst_rate.total}%: ₹${gstAmount.toFixed(2)})`)
          .align('left')
      }

      builder.feed()
    }

    builder.separator()
  }

  /**
   * Add totals section
   */
  _addTotals(builder, transaction) {
    // Subtotal
    if (this.includeGST && transaction.subtotal) {
      builder.textLine('Subtotal:', `₹${transaction.subtotal.toFixed(2)}`)
      
      // GST breakdown
      if (transaction.gst_details) {
        const gst = transaction.gst_details
        if (gst.cgst > 0) builder.textLine('CGST:', `₹${gst.cgst.toFixed(2)}`)
        if (gst.sgst > 0) builder.textLine('SGST:', `₹${gst.sgst.toFixed(2)}`)
        if (gst.igst > 0) builder.textLine('IGST:', `₹${gst.igst.toFixed(2)}`)
      }
    }

    // Total
    builder.bold(true)
      .size(1, 2)
      .textLine('TOTAL:', `₹${transaction.total.toFixed(2)}`)
      .reset()

    // Payment method
    if (transaction.payment_method) {
      builder.textLine('Payment:', transaction.payment_method)
    }

    builder.separator('=')
  }

  /**
   * Add receipt footer
   */
  _addFooter(builder, transaction) {
    if (this.includeBranding) {
      builder.align('center')
        .text('Thank you for your business!')
        .feed()
        .text('Powered by QuickBill POS')
    }

    builder.feed(2)
  }
}

/**
 * Main Printer Utility Class
 * Orchestrates all printing operations
 */
class PrinterUtility extends EventTarget {
  constructor(options = {}) {
    super()
    
    this.config = {
      paperWidth: options.paperWidth || 58,
      autoReconnect: options.autoReconnect ?? true,
      cutPaper: options.cutPaper ?? true,
      includeGST: options.includeGST ?? true,
      includeBranding: options.includeBranding ?? true,
      retryAttempts: options.retryAttempts || 3,
      debug: options.debug ?? false,
      ...options
    }

    // Initialize managers
    this.bluetoothManager = new BluetoothPrinterManager({
      autoReconnect: this.config.autoReconnect,
      retryAttempts: this.config.retryAttempts,
      debug: this.config.debug
    })

    this.formatter = new ReceiptFormatter({
      paperWidth: this.config.paperWidth,
      includeGST: this.config.includeGST,
      includeBranding: this.config.includeBranding
    })

    // Forward events from Bluetooth manager
    this._setupEventForwarding()
  }

  /**
   * Setup event forwarding from Bluetooth manager
   */
  _setupEventForwarding() {
    const events = ['scanning', 'devicesFound', 'connecting', 'connected', 'disconnected', 'connectionError', 'reconnectionFailed']
    
    events.forEach(eventName => {
      this.bluetoothManager.addEventListener(eventName, (event) => {
        this.dispatchEvent(new CustomEvent(eventName, { detail: event.detail }))
      })
    })
  }

  /**
   * Check if Bluetooth printing is supported
   */
  isSupported() {
    return this.bluetoothManager.isBluetoothSupported()
  }

  /**
   * Discover available printers
   */
  async discoverPrinters() {
    return await this.bluetoothManager.discoverPrinters()
  }

  /**
   * Connect to a printer
   */
  async connect(device) {
    return await this.bluetoothManager.connectToDevice(device)
  }

  /**
   * Disconnect from current printer
   */
  async disconnect() {
    return await this.bluetoothManager.disconnect()
  }

  /**
   * Print a receipt
   */
  async printReceipt(transaction, business = {}) {
    this.dispatchEvent(new CustomEvent('printing', { detail: transaction }))

    try {
      // Update business info for formatter
      this.formatter.business = business

      // Format receipt
      const commands = this.formatter.formatReceipt(transaction, {
        cut: this.config.cutPaper
      })

      // Send to printer
      await this.bluetoothManager.sendData(commands)

      this.dispatchEvent(new CustomEvent('printed', { detail: transaction }))
      return true

    } catch (error) {
      this.dispatchEvent(new CustomEvent('printError', { detail: error }))
      throw error
    }
  }

  /**
   * Print receipt using template engine (NEW)
   */
  async printReceiptWithTemplate(templateId, transaction, business = {}, options = {}) {
    this.dispatchEvent(new CustomEvent('printing', { detail: { transaction, templateId } }))

    try {
      const engine = await getTemplateEngine()
      
      // Validate template compatibility
      engine.validateTemplate(templateId, this.config.paperWidth)
      
      // Generate receipt commands using template
      const commands = await engine.generateReceipt(templateId, transaction, business, {
        cutPaper: this.config.cutPaper,
        ...options
      })

      // Send to printer
      await this.bluetoothManager.sendData(commands)

      this.dispatchEvent(new CustomEvent('printed', { 
        detail: { transaction, templateId, success: true } 
      }))
      return true

    } catch (error) {
      this.dispatchEvent(new CustomEvent('printError', { 
        detail: { error, transaction, templateId } 
      }))
      throw error
    }
  }

  /**
   * Get available templates for current paper width
   */
  async getAvailableTemplates() {
    const engine = await getTemplateEngine()
    return engine.getTemplatesByPaperWidth(this.config.paperWidth)
  }

  /**
   * Get recommended template for current configuration
   */
  async getRecommendedTemplate(category = 'detailed') {
    const engine = await getTemplateEngine()
    return engine.getRecommendedTemplate(this.config.paperWidth, category)
  }

  /**
   * Print raw ESC/POS commands
   */
  async printRaw(commands) {
    const data = typeof commands === 'string' 
      ? new TextEncoder().encode(commands)
      : commands

    return await this.bluetoothManager.sendData(data)
  }

  /**
   * Get printer status
   */
  getStatus() {
    return this.bluetoothManager.getStatus()
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig }
    
    // Update formatter
    this.formatter.paperWidth = this.config.paperWidth
    this.formatter.includeGST = this.config.includeGST
    this.formatter.includeBranding = this.config.includeBranding
  }

  /**
   * Test printer connection with a simple print
   */
  async testPrint() {
    const builder = new ESCPOSCommandBuilder({
      paperWidth: this.config.paperWidth
    })

    const testReceipt = builder
      .reset()
      .align('center')
      .bold(true)
      .text('TEST PRINT')
      .reset()
      .feed()
      .text('Printer is working correctly!')
      .feed()
      .align('center')
      .text(`Paper Width: ${this.config.paperWidth}mm`)
      .text(`Time: ${new Date().toLocaleString()}`)
      .separator()
      .text('QuickBill POS System')
      .feed(3)

    if (this.config.cutPaper) {
      builder.cut()
    }

    return await this.printRaw(builder.build())
  }

  /**
   * Cleanup printer resources and optimize for next print
   */
  async optimizeForNextPrint() {
    try {

      
      // Clear any cached data
      if (this.lastCommand) {
        this.lastCommand = null
      }
      
      // Reset printer state
      if (this.bluetoothManager?.isConnected) {
        const resetCommand = new Uint8Array([0x1B, 0x40]) // ESC @
        await this.bluetoothManager.sendData(resetCommand)
      }
      
      // Clear image cache if using text-to-image
      const { defaultTextImageConverter } = await import('./textImageConverter.js')
      if (defaultTextImageConverter && defaultTextImageConverter.clearCache) {
        defaultTextImageConverter.manageCacheSize() // Only clean old entries, not all
      }
      
      // Force garbage collection if available
      if (typeof window !== 'undefined' && window.gc) {
        window.gc()
      }
      

      
    } catch (error) {
      console.warn('⚠️ Failed to optimize printer:', error.message)
    }
  }

  /**
   * Full cleanup - call when done with printer session
   */
  async dispose() {
    try {

      
      // Clear image cache completely
      const { defaultTextImageConverter } = await import('./textImageConverter.js')
      if (defaultTextImageConverter && defaultTextImageConverter.dispose) {
        defaultTextImageConverter.dispose()
      }
      
      // Disconnect from printer
      if (this.bluetoothManager) {
        await this.bluetoothManager.disconnect()
      }
      
      // Clear all references
      this.lastCommand = null
      this.config = null
      

      
    } catch (error) {
      console.warn('⚠️ Failed to dispose printer resources:', error.message)
    }
  }
}

/**
 * Global optimization functions
 */
const PrinterOptimizations = {
  /**
   * Optimize all printer resources after batch printing
   */
  async optimizeAfterBatch() {
    // Clear image caches
    try {
      const { defaultTextImageConverter } = await import('./textImageConverter.js')
      if (defaultTextImageConverter?.clearCache) {
        defaultTextImageConverter.clearCache()
      }
    } catch (error) {
      console.warn('Failed to clear image cache:', error)
    }
    
    // Force garbage collection
    if (typeof window !== 'undefined' && window.gc) {
      window.gc()
    }
    

  },

  /**
   * Prepare printer for optimal performance
   */
  async prepareForOptimalPerformance() {
    // Pre-warm image converter
    try {
      const { defaultTextImageConverter } = await import('./textImageConverter.js')
      if (defaultTextImageConverter?.manageCacheSize) {
        defaultTextImageConverter.manageCacheSize()
      }
    } catch (error) {
      console.warn('Failed to prepare image converter:', error)
    }
    

  }
}

// Export classes for use
export {
  PrinterUtility,
  BluetoothPrinterManager,
  ESCPOSCommandBuilder,
  ReceiptFormatter,
  PrinterError,
  BluetoothError,
  ConnectionError,
  ESCPOS_COMMANDS,
  PrinterOptimizations
}

// Default export
export default PrinterUtility